import { useEffect, useState, useCallback } from 'react';
import FilterSearch from '../../../components/FilterSearch/FilterSearch';
import { Button, Spinner, Table } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useUrlSearchParams } from 'use-url-search-params';
import { fetchAllItems } from './actions/action';
import DHPagination from '../../../components/Pagination';

const ItemList = ({ onEdit, onDisable, refreshTrigger }) => {
  const dispatch = useDispatch();
  const [paginationValue, setPaginationValue] = useUrlSearchParams({ page: 0 });
  const [limitValue, setLimitValue] = useUrlSearchParams({ limit: 10 });
  const [serviceItemData, setServiceItemData] = useState([]);
  // State for FilterSearch
  const [searchFilter, setSearchFilter] = useState({ search: '' });
  const [statusFilter, setStatusFilter] = useState({ status: 'All' });
  const [advancedFilter, setAdvancedFilter] = useState({});

  // State for AMC Toggle
  const [expandedItems, setExpandedItems] = useState({});

  const { serviceItemList = [], isLoading } = useSelector(
    (state) => state.service,
  );

  useEffect(() => {
    if (serviceItemList && Array.isArray(serviceItemList)) {
      setServiceItemData(serviceItemList);
    } else if (serviceItemList && Array.isArray(serviceItemList.items)) {
      setServiceItemData(serviceItemList.items);
    } else {
      setServiceItemData([]);
    }
  }, [serviceItemList]);

  const [isDataLoading, setDataLoading] = useState(false);

  const getServiceItemList = useCallback(
    async (data) => {
      try {
        setDataLoading(true);
        await fetchAllItems({
          params: data,
          dispatch,
        });
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setDataLoading(false);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    const delay = setTimeout(() => {
      getServiceItemList({
        start: Number(paginationValue.page * limitValue.limit),
        limit: Number(limitValue.limit),
        sort: {
          value: 'id',
          order: 1,
        },
        search_qry: searchFilter?.search,
        product_type: statusFilter?.status,
        advancedFilter,
      });
    }, 300);
    return () => clearTimeout(delay);
  }, [
    paginationValue.page,
    limitValue.limit,
    searchFilter,
    statusFilter,
    advancedFilter,
    getServiceItemList,
    refreshTrigger,
  ]);

  const toggleAmcItems = (itemId) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  return (
    <>
      <FilterSearch
        setAdvancedFilter={setAdvancedFilter}
        setSearchFilter={setSearchFilter}
        setStatusFilter={setStatusFilter}
        filterType="serviceItem"
      />
      <div className="dt-table">
        <div className="table-responsive">
          <Table className="OrderListTable table mb-0">
            <thead>
              <tr className="align-middle">
                <th>SI.No</th>
                <th>Image</th>
                <th>Title</th>
                <th>SKU</th>
                <th>Type</th>
                <th>Model</th>
                <th>Unit Price</th>
                <th className="text-center">Default</th>
                <th className="text-center">AMC Items</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            {isLoading || isDataLoading ? (
              <tbody>
                {[...Array(2)].map((_, index) => (
                  <tr key={index} className="align-middle">
                    <td>
                      <p className="placeholder-glow">
                        <span className="placeholder col-2"></span>
                      </p>
                    </td>
                    <td>
                      <p className="placeholder-glow">
                        <span className="placeholder col-6 h-100"></span>
                      </p>
                    </td>
                    <td>
                      <p className="placeholder-glow d-flex flex-column gap-1">
                        <span className="placeholder col-6"></span>
                        <span className="placeholder w-75"></span>
                        <span className="placeholder col-3"></span>
                      </p>
                    </td>
                    <td>
                      <p className="placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </p>
                    </td>
                    <td>
                      <p className="placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </p>
                    </td>
                    <td>
                      <p className="placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </p>
                    </td>
                    <td>
                      <p className="placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </p>
                    </td>
                    <td className="text-center">
                      <p className="placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </p>
                    </td>
                    <td className="text-center">
                      <p className="placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </p>
                    </td>
                    <td className="text-center">
                      <p className="placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
            ) : serviceItemData?.length > 0 ? (
              serviceItemData?.map((item, index) => (
                <tbody
                  key={index}
                  className={`${
                    item?.isAmc && item?.isAmc === '1' ? 'bg-soft-light' : ''
                  }`}
                >
                  <tr className="align-middle">
                    <td>{index + 1}</td>
                    <td>
                      <img
                        src={item?.imgUrl || '/images/no-image.jpg'}
                        alt=""
                        height="70"
                        width="70"
                        className="rounded-3 border border-1 #dedede"
                      />
                    </td>
                    <td>{item?.title}</td>
                    <td>{item?.sku}</td>
                    <td>{item?.productType}</td>
                    <td>{item?.model}</td>
                    <td>{item?.price}</td>
                    <td className="text-center">
                      {String(item?.productType || '').toLowerCase() === 'service' ? (
                        String(item?.isDefaultSelection ?? item?.is_default_selection ?? '0') === '1' ? (
                          <span className="badge bg-soft-success text-success">
                            <i className="mdi mdi-check-circle-outline me-1"></i>
                            Yes
                          </span>
                        ) : (
                          <span className="badge bg-soft-danger text-danger">
                            <i className="mdi mdi-close-circle-outline me-1"></i>
                            No
                          </span>
                        )
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="text-center">
                      {item?.isAmc && item?.isAmc === '1' ? (
                        <Button
                          color="soft-info"
                          size="sm"
                          onClick={() => toggleAmcItems(item.id || index)}
                        >
                          AMC Items
                        </Button>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="text-center">
                      <Button
                        color="light"
                        size="sm"
                        onClick={() => onEdit(item)}
                        className="me-2 text-dark"
                      >
                        <i className="fa fa-edit me-2"></i>
                        Edit
                      </Button>
                      <Button
                        color={String(item?.status) === '1' ? 'soft-warning' : 'soft-success'}
                        size="sm"
                        onClick={() => onDisable(item.id, String(item?.status) === '1' ? 0 : 1)}
                      >
                        <i className={`fa ${String(item?.status) === '1' ? 'fa-ban' : 'fa-check-circle'} me-2`}></i>
                        {String(item?.status) === '1' ? 'Disable' : 'Enable'}
                      </Button>
                    </td>
                  </tr>

                  {item?.isAmc &&
                    item?.isAmc === '1' &&
                    expandedItems[item.id || index] && (
                      <tr>
                        <td colSpan={10} className="p-0">
                          <Table>
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Item Name</th>
                                <th>Sku</th>
                                <th>Was Price</th>
                                <th>Price</th>
                              </tr>
                            </thead>
                            <tbody>
                              {item?.amcItems?.map((amcItem, amcIndex) => (
                                <tr key={amcIndex}>
                                  <td>{amcIndex + 1}</td>
                                  <td>{amcItem?.title}</td>
                                  <td>{amcItem?.sku}</td>
                                  <td>{amcItem?.wasPrice ?? amcItem?.was_price ?? '-'}</td>
                                  <td>{amcItem?.price}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </td>
                      </tr>
                    )}
                </tbody>
              ))
            ) : (
              <tbody>
                <tr>
                  <td colSpan={10}>No Records found</td>
                </tr>
              </tbody>
            )}
          </Table>
        </div>

        {serviceItemData?.length > 0 ? (
          <DHPagination
            totalPages={Math.ceil(
              (serviceItemList?.pagination?.total || 0) /
                Number(limitValue.limit || 10),
            )}
            currentPage={paginationValue.page}
            setPaginationValue={setPaginationValue}
            setLimitValue={setLimitValue}
          />
        ) : null}
      </div>
    </>
  );
};

export default ItemList;
